import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { BudgetI } from "components/dashboard/body/projects/panels/budget/types";
import { useVersionSettings } from "hooks/useProjects/useVersions";
import { AppState } from "store/reducers/Reducer";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { parseVersionProductionLocation } from "utils/budgetAssumptionsMapping";
import {
  BudgetDisplayLocationResult,
  resolveBudgetDisplayLocation,
} from "utils/resolveBudgetDisplayLocation";

function readProductionStateFromVersionPayload(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (typeof record.production_state === "string") return record.production_state;
  const settings = record.settings;
  if (settings && typeof settings === "object" && !Array.isArray(settings)) {
    const ps = (settings as Record<string, unknown>).production_state;
    if (typeof ps === "string") return ps;
  }
  return null;
}

function readProductionLocationFromVersionPayload(data: unknown) {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (record.production_location) {
    return parseVersionProductionLocation(record.production_location);
  }
  const settings = record.settings;
  if (settings && typeof settings === "object" && !Array.isArray(settings)) {
    return parseVersionProductionLocation(
      (settings as Record<string, unknown>).production_location
    );
  }
  return null;
}

/** Production base and tax state for the active budget, from budget, version and project data. */
export function useBudgetDisplayLocation(
  budget: Pick<BudgetI, "rerun_params" | "assumptions"> | null | undefined
): BudgetDisplayLocationResult {
  const { projectId, documentId } = useProjectIdentifiers();
  const versionSettingsQuery = useVersionSettings(projectId, documentId ?? undefined);
  const projectProductionState = useSelector(
    (state: AppState) => state.project.features?.production_state
  );

  return useMemo(
    () =>
      resolveBudgetDisplayLocation({
        rerunParams: budget?.rerun_params,
        assumptions: budget?.assumptions,
        versionProductionState: readProductionStateFromVersionPayload(versionSettingsQuery.data),
        versionProductionLocation: readProductionLocationFromVersionPayload(
          versionSettingsQuery.data
        ),
        projectProductionState,
      }),
    [budget?.assumptions, budget?.rerun_params, projectProductionState, versionSettingsQuery.data]
  );
}
